import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui';
import { createService } from '../../services/serviceService';
import { v4 as uuidv4 } from 'uuid';
import { SERVICE_CATEGORIES } from '../../constants/categories';

// Componente Tooltip
const InfoTooltip = ({ text }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  return (
    <div className="relative inline-block ml-2">
      <button
        type="button"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className="inline-flex items-center justify-center w-4 h-4 text-xs text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      </button>
      {showTooltip && (
        <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 px-3 py-2 text-xs text-white bg-gray-900 dark:bg-slate-800 rounded-lg shadow-lg">
          <div className="relative">
            {text}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
              <div className="border-4 border-transparent border-t-gray-900 dark:border-t-slate-800"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DELIVERY_TIMES = [
  '24 horas',
  '1-2 días',
  '3-5 días',
  '1 semana',
  '2 semanas',
  '3-4 semanas',
  '1 mes',
  '2 meses',
  'A convenir'
];

const CreateServiceModal = ({ onClose, onServiceCreated }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // Estado del formulario
  const [formData, setFormData] = useState({
    caption: '',
    description: '',
    category: 'digital-art',
    deliveryTime: '3-5 días',
    specifications: '',
    terms: '',
    allowClientDescription: true,
    allowCustomizations: true,
    currency: 'ARS'
  });

  // Paquetes de precios
  const [packages, setPackages] = useState([
    { id: uuidv4(), details: '', price: '', deliveryTime: '3-5 días' }
  ]);

  // Imágenes
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + selectedImages.length > 5) {
      setError('Máximo 5 imágenes por servicio');
      return;
    }

    setSelectedImages(prev => [...prev, ...files]);
    
    // Crear previews
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const addPackage = () => {
    if (packages.length >= 5) {
      setError('Máximo 5 paquetes por servicio');
      return;
    }
    setPackages([...packages, { id: uuidv4(), details: '', price: '', deliveryTime: '3-5 días' }]);
  };

  const removePackage = (id) => {
    if (packages.length === 1) {
      setError('Debe haber al menos un paquete');
      return;
    }
    setPackages(packages.filter(pkg => pkg.id !== id));
  };

  const updatePackage = (id, field, value) => {
    setPackages(packages.map(pkg => 
      pkg.id === id ? { ...pkg, [field]: value } : pkg
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validaciones
    if (!formData.caption.trim()) {
      setError('El título es obligatorio');
      return;
    }

    if (!formData.description.trim()) {
      setError('La descripción es obligatoria');
      return;
    }

    if (selectedImages.length === 0) {
      setError('Debes agregar al menos una imagen');
      return;
    }

    // Validar paquetes
    const validPackages = packages.filter(pkg => pkg.details.trim() && pkg.price);
    if (validPackages.length === 0) {
      setError('Debes agregar al menos un paquete con precio');
      return;
    }

    setIsSubmitting(true);

    try {
      // Preparar paquetes con precios parseados
      const formattedPackages = validPackages.map(pkg => ({
        id: pkg.id,
        details: pkg.details,
        price: parseFloat(pkg.price),
        deliveryTime: pkg.deliveryTime
      }));

      const serviceData = {
        ...formData,
        prices: formattedPackages,
        servicePrice: formattedPackages[0].price // Precio del primer paquete como precio base
      };

      const result = await createService(serviceData, selectedImages);

      if (result.success) {
        onServiceCreated(result.service);
        onClose();
      } else {
        setError(result.error || 'Error al crear el servicio');
      }
    } catch (err) {
      setError(err.message || 'Error al crear el servicio');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            💼 Crear Nuevo Servicio
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-full transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-200">
              {error}
              {(String(error).toLowerCase().includes('límite') || String(error).toLowerCase().includes('limite')) && (
                <div className="mt-2 text-sm">
                  <Link to="/premium" className="font-semibold underline underline-offset-2">
                    Ver Premium
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* Título */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Título del servicio *
            </label>
            <input
              type="text"
              name="caption"
              value={formData.caption}
              onChange={handleInputChange}
              placeholder="Ej: Ilustraciones estilo anime"
              className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
              maxLength={100}
            />
          </div>

          {/* Descripción */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Descripción *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Describe tu servicio en detalle..."
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 resize-none"
            />
          </div>

          {/* Categoría */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Categoría
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
            >
              {SERVICE_CATEGORIES.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          {/* Imágenes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Imágenes de ejemplo * (máximo 5)
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              📐 Resolución recomendada: <span className="font-semibold text-indigo-600 dark:text-indigo-400">1920x1080px</span> o superior (ratio 16:9)
              <br />
              Formatos: JPG, PNG, WebP | Tamaño máximo: 5MB por imagen
            </p>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageSelect}
              className="hidden"
              id="service-images"
            />
            <label
              htmlFor="service-images"
              className="flex items-center justify-center w-full p-4 border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg cursor-pointer hover:border-indigo-500 transition-colors"
            >
              <div className="text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  Click para agregar imágenes
                </p>
              </div>
            </label>

            {/* Image Previews */}
            {imagePreviews.length > 0 && (
              <div className="grid grid-cols-5 gap-2 mt-4">
                {imagePreviews.map((preview, index) => (
                  <div key={index} className="relative aspect-square">
                    <img
                      src={preview}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-full object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Paquetes de precios */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Paquetes y precios *
              </label>
              <button
                type="button"
                onClick={addPackage}
                className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                + Agregar paquete
              </button>
            </div>
            <div className="space-y-3">
              {packages.map((pkg, index) => (
                <div key={pkg.id} className="p-4 border border-gray-300 dark:border-slate-600 rounded-lg space-y-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Paquete {index + 1}
                    </span>
                    {packages.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removePackage(pkg.id)}
                        className="text-red-600 hover:text-red-700 text-sm"
                      >
                        Eliminar
                      </button>
                    )}
                  </div>
                  <input
                    type="text"
                    value={pkg.details}
                    onChange={(e) => updatePackage(pkg.id, 'details', e.target.value.slice(0, 160))}
                    maxLength={160}
                    placeholder="Título del paquete (máx. 160 caracteres)"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm"
                  />
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 text-right">
                    {(pkg.details || '').length}/160
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      value={pkg.price}
                      onChange={(e) => updatePackage(pkg.id, 'price', e.target.value)}
                      placeholder="Precio (en ARS)"
                      className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm"
                    />
                    <select
                      value={pkg.deliveryTime}
                      onChange={(e) => updatePackage(pkg.id, 'deliveryTime', e.target.value)}
                      className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm"
                    >
                      {DELIVERY_TIMES.map(time => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tiempo de entrega general */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tiempo de entrega general
            </label>
            <select
              name="deliveryTime"
              value={formData.deliveryTime}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
            >
              {DELIVERY_TIMES.map(time => (
                <option key={time} value={time}>{time}</option>
              ))}
            </select>
          </div>

          {/* Especificaciones */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Especificaciones
            </label>
            <textarea
              name="specifications"
              value={formData.specifications}
              onChange={handleInputChange}
              placeholder="Detalles técnicos, formatos de entrega, etc."
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 resize-none"
            />
          </div>

          {/* Términos y condiciones */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Términos y condiciones
            </label>
            <textarea
              name="terms"
              value={formData.terms}
              onChange={handleInputChange}
              placeholder="Políticas de reembolso, revisiones permitidas, etc."
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 resize-none"
            />
          </div>

          {/* Opciones */}
          <div className="space-y-3">
            <div className="flex items-start">
              <label className="flex items-center space-x-2 flex-1">
                <input
                  type="checkbox"
                  name="allowClientDescription"
                  checked={formData.allowClientDescription}
                  onChange={handleInputChange}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 mt-0.5"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center">
                  Permitir descripción personalizada del cliente
                  <InfoTooltip text="Permite que el cliente escriba sus propias instrucciones y detalles específicos al momento de solicitar el servicio. Útil para servicios personalizados donde cada pedido es único (ej: comisiones, ilustraciones custom)." />
                </span>
              </label>
            </div>
            <div className="flex items-start">
              <label className="flex items-center space-x-2 flex-1">
                <input
                  type="checkbox"
                  name="allowCustomizations"
                  checked={formData.allowCustomizations}
                  onChange={handleInputChange}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 mt-0.5"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center">
                  Permitir personalizaciones
                  <InfoTooltip text="Permite que el cliente solicite ajustes, cambios o elementos adicionales fuera de lo especificado en el paquete base. Esto puede incluir revisiones extras, cambios de color, agregado de personajes, etc. Puedes definir límites en los términos y condiciones." />
                </span>
              </label>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-slate-700">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creando...' : 'Crear Servicio'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateServiceModal;
